import { Component, OnInit, inject, signal, computed, effect, DestroyRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { RequestService, Request } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { RequestClaimComponent } from '../../components/request-claim/request-claim.component';
import { RequestBenefitComponent } from '../../components/request-benefit/request-benefit.component';
import { RequestPartyComponent } from '../../components/request-party/request-party.component';
import { RequestTransferComponent } from '../../components/request-transfer/request-transfer.component';
import { RequestPaymentComponent } from '../../components/request-payment/request-payment.component';
import { RequestListComponent } from '../../components/request-list/request-list.component';
import { AddClaimComponent } from '../../../management-claim/pages/add-claim/add-claim.component';
import { AddPartyComponent } from '../../../management-parties/pages/add-party/add-party.component';
import { FormMode } from 'app/core/constants/form-mode';
import { CatalogService } from '@services/data/catalog.service';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MasterDetailNav } from 'app/core/service/utils/master-detail-nav.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { NotificationHubService } from '@services/core/notification-hub.service';
import { AdvancedFilterComponent, FilterField } from 'app/core/components/advanced-filter/advanced-filter.component';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { FlowBadgeComponent } from 'app/core/components/flow-badge/flow-badge.component';

@Component({
  selector: 'app-requests',
  standalone: true,
  imports: [
    CommonModule, FormsModule, NgSelectModule, RouterLink,
    RequestClaimComponent, RequestBenefitComponent, RequestPartyComponent, RequestTransferComponent, RequestPaymentComponent,
    RequestListComponent, AddClaimComponent, AddPartyComponent,
    AdvancedFilterComponent, PageHeaderComponent, MobileFooterComponent, FlowBadgeComponent
  ],
  templateUrl: './requests.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./requests.component.scss']
})
export class RequestsComponent implements OnInit {
  public appSettings = inject(AppSettings);
  private requestService = inject(RequestService);
  private bpmService = inject(BpmService);
  private catalogService = inject(CatalogService);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private hub = inject(NotificationHubService);
  private md = inject(MasterDetailNav);

  readonly formMode = FormMode;

  allRequests = this.requestService.requests;
  isLoading = this.requestService.isLoading;

  selectedItems = signal<string[]>([]);
  /** Filtro activo por processSlug (badge). No limpiar al seleccionar una solicitud. */
  activeTypeFilter = signal<string | null>(null);

  selectedRequest = signal<Request | null>(null);
  searchQuery = signal('');
  claimEditorTitle = signal<string | null>(null);
  @ViewChild(RequestClaimComponent) requestClaim?: RequestClaimComponent;
  @ViewChild(AddClaimComponent) addClaim?: AddClaimComponent;

  get showListMobileActions(): boolean {
    return this.appSettings.isMobile() && !this.selectedRequest() && !this.claimEditorTitle();
  }

  /** Lista maestra visible: desktop (salvo editor full) o móvil sin detalle. */
  get showMasterList(): boolean {
    if (this.claimEditorTitle()) return false;
    if (this.appSettings.isMobile()) return !this.selectedRequest();
    return true;
  }

  pageHeaderTitle = computed(() => {
    if (this.claimEditorTitle()) return this.claimEditorTitle()!;
    const req = this.selectedRequest();
    if (req) return this.getProcessName(req.processSlug);
    return this.appSettings.isMobile() ? 'Solicitudes' : 'Bandeja de Solicitudes';
  });

  /**
   * Vista de detalle por categoría del proceso (process_ref.category).
   * Así un proceso nuevo del catálogo entra sin agregar otro @case por slug.
   */
  detailKind = computed(() => {
    const req = this.selectedRequest();
    if (!req?.processSlug) return null;
    const cat = this.bpmService.processCategory(req.processSlug);
    if (cat === 'TRANSFER') return 'transfer';
    if (cat === 'CLAIM') return 'claim';
    if (cat === 'BENEFITS') return 'benefit';
    if (cat === 'PAYMENT') return 'payment';
    if (cat === 'VEHICLE' || cat === 'VEHICLE_UPDATE' || cat === 'MEMBERSHIP' || cat === 'UNSUBSCRIBE'
      || cat === 'UNSUBSCRIBE_PARTY' || cat === 'UNSUBSCRIBE_ADM') return 'party';
    return 'claim';
  });

  /** Solicitud seleccionada aún en armado (borrador). */
  isDraftDetail = computed(() =>
    String(this.selectedRequest()?.statusSlug || '').toUpperCase() === 'DRAFT'
  );

  /**
   * Qué wizard embeber en DRAFT.
   * CLAIM → add-claim; MEMBERSHIP/VEHICLE → add-party.
   * Usa categoría del request/proceso (no solo catálogo UI, que a veces aún no cargó).
   */
  draftWizardKind = computed<'claim' | 'party' | null>(() => {
    if (!this.isDraftDetail()) return null;
    const cat = this.resolveDraftCategory(this.selectedRequest());
    if (cat === 'CLAIM') return 'claim';
    if (cat === 'MEMBERSHIP' || cat === 'VEHICLE') return 'party';
    return null;
  });

  /** Categoría para decidir wizard DRAFT: catálogo → process del request → slug. */
  private resolveDraftCategory(req: Request | null): string | null {
    if (!req?.processSlug) return null;
    const fromCatalog = this.bpmService.processCategory(req.processSlug);
    if (fromCatalog) return fromCatalog;
    const fromReq = String((req as any)?.process?.category || '').trim().toUpperCase();
    if (fromReq) return fromReq;
    const slug = String(req.processSlug).toUpperCase();
    if (slug.includes('ACCIDENT') || slug.includes('TOTAL_LOSS') || slug === 'CLAIM') return 'CLAIM';
    if (slug.includes('MEMBERSHIP') || slug === 'VEHICLE_NEW') return 'MEMBERSHIP';
    return null;
  }

  draftClaimParty = computed(() => this.selectedRequest()?.party ?? null);

  draftClaimVehicles = computed(() => {
    const req = this.selectedRequest();
    const vehicle = req?.claim?.vehicle ?? req?.vehicle ?? null;
    return vehicle ? [vehicle] : [];
  });

  draftClaimUuid = computed(() =>
    (this.selectedRequest()?.claim as any)?.uuid ?? null
  );

  draftPartyVehicle = computed(() =>
    this.selectedRequest()?.vehicle ?? null
  );

  advancedFilterFields: FilterField[] = [
    { key: 'party', label: 'Socio / RUT' },
    { key: 'plate', label: 'Patente' },
    { key: 'folio', label: 'Folio / UUID' },
    { key: 'entityCode', label: 'Código Entidad' }
  ];
  activeAdvancedFilters = signal<Array<{ field: string; value: string }>>([]);

  viewableRequests = computed(() => {
    return (this.allRequests() || []).filter(r => !this.bpmService.isClosed(r));
  });

  // Filtros derivados del catálogo: cualquier proceso listado se puede filtrar.
  // El filtro activo se conserva aunque su proceso desaparezca, para poder desactivarlo.
  typeFilters = computed(() => {
    const present = new Set(this.viewableRequests().map(r => r.processSlug));
    const active = this.activeTypeFilter();
    if (active) present.add(active);
    return this.catalogService.processes()
      .filter(p => present.has(p.slug))
      .map(p => ({ slug: p.slug, name: p.name, icon: p.icon }));
  });

  private processIndex = computed(() => {
    const index = new Map<string, any>();
    this.catalogService.processes().forEach(p => {
      index.set(p.slug, p);
      if (p.code) index.set(p.code, p);
    });
    return index;
  });

  // Comprueba coincidencia en campos de texto de la solicitud
  private _matchRequestField(r: Request, field: string, val: string): boolean {
    if (field === 'plate') {
      const plate = (r.vehicle?.plate ?? r.claim?.vehicle?.plate ?? r.benefit?.vehicle?.plate ?? '').toLowerCase();
      return plate.includes(val);
    }
    const target = field === 'folio' ? r.uuid : r.entityCode;
    return String(target ?? '').toLowerCase().includes(val);
  }

  // Evalúa si una solicitud cumple una regla del filtro avanzado
  private _matchAdvancedRule(r: Request, rule: { field: string; value: string }): boolean {
    const val = (rule?.value ?? '').toLowerCase();
    if (!val) return true;
    if (rule.field === 'party') {
      const partyStr = `${r.party?.names ?? ''} ${r.party?.lastNames ?? ''} ${r.party?.rut ?? ''}`.toLowerCase();
      return partyStr.includes(val);
    }
    return this._matchRequestField(r, rule.field, val);
  }

  // Aplica la lista de filtros avanzados activos
  private _applyAdvancedFilters(list: Request[]): Request[] {
    const filters = this.activeAdvancedFilters() ?? [];
    if (!filters.length) return list;
    return list.filter(r => filters.every(rule => this._matchAdvancedRule(r, rule)));
  }

  // Filtra por texto libre en buscador rápido
  private _filterByQuery(list: Request[]): Request[] {
    const q = (this.searchQuery() ?? '').toLowerCase().trim();
    if (!q) return list;
    return list.filter(r => {
      const party = `${r.party?.names ?? ''} ${r.party?.lastNames ?? ''} ${r.party?.rut ?? ''}`.toLowerCase();
      const plate = (r.vehicle?.plate ?? r.claim?.vehicle?.plate ?? '').toLowerCase();
      const entity = String(r.entityCode ?? '').toLowerCase();
      const uuid = String(r.uuid ?? '').toLowerCase();
      return party.includes(q) || plate.includes(q) || entity.includes(q) || uuid.includes(q);
    });
  }

  filteredRequests = computed(() => {
    let list = this.viewableRequests();

    if (this.activeTypeFilter()) {
      list = list.filter(item => item.processSlug === this.activeTypeFilter());
    }
    const selectedUuids = this.selectedItems() || [];
    if (selectedUuids.length > 0) {
      list = list.filter(item => selectedUuids.includes(item.uuid as any));
    }
    list = this._filterByQuery(list);
    return this._applyAdvancedFilters(list);
  });

  constructor() {
    effect(() => {
      const list = this.allRequests();
      const current = this.selectedRequest();
      if (!current) return;
      const updated = list.find(r => r.uuid === current.uuid);
      if (updated && updated !== current) {
        this.selectedRequest.set(updated);
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const uuid = this.requestService.focusRequestUuid();
      if (!uuid) return;
      this.md.go(this.md.request(uuid));
      this.requestService.clearFocusRequest();
    });

    this.md.urlChanges$.pipe(takeUntilDestroyed()).subscribe(() => this.syncFromRoute());
  }

  ngOnInit() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const uuid = params.get('uuid') ?? params.get('id');
        if (uuid && !this.md.parse().requestUuid) {
          this.md.replace(this.md.request(uuid));
        }
      });
    this.loadRequests();
  }

  getProcessName(slug: string): string {
    if (!slug) return 'Proceso';
    return this.processIndex().get(slug)?.name ?? `Proceso ${slug}`;
  }

  loadRequests() {
    this.requestService.loadRequests();
  }

  selectRequest(app: Request) {
    if (!app?.uuid) return;
    const st = this.md.parse();
    if (st.requestUuid === app.uuid) {
      this.applyRequest(app);
      return;
    }
    this.md.go(this.md.request(app.uuid));
  }

  private applyRequest(app: Request) {
    this.selectedRequest.set(app);
    this.hub.markOpened(app);
    this.requestService.getRequestByUuid(app.uuid).subscribe({
      next: (full) => {
        if (!full?.uuid || this.selectedRequest()?.uuid !== full.uuid) return;
        this.selectedRequest.set(full);
        const list = this.requestService.requests();
        const idx = list.findIndex((r) => r.uuid === full.uuid);
        if (idx >= 0) {
          const next = list.slice();
          next[idx] = full;
          this.requestService.requests.set(next);
        }
      },
      error: () => {}
    });
  }

  private syncFromRoute() {
    const st = this.md.parse();
    if (st.area !== 'solicitudes') return;
    if (!st.requestUuid) {
      this.selectedRequest.set(null);
      this.claimEditorTitle.set(null);
      return;
    }
    if (this.selectedRequest()?.uuid === st.requestUuid) {
      if (!st.dano && this.claimEditorTitle()) this.requestClaim?.closeDetailEditor();
      return;
    }
    const list = this.allRequests();
    const target = list.find(r => r.uuid === st.requestUuid || String((r as any).id) === st.requestUuid);
    if (target) {
      this.applyRequest(target);
      return;
    }
    this.requestService.getRequestByUuid(st.requestUuid).subscribe({
      next: (full) => {
        if (this.md.parse().requestUuid !== full?.uuid) return;
        this.selectedRequest.set(full);
      },
      error: () => {}
    });
  }

  onClaimEditorTitle(title: string | null) {
    this.claimEditorTitle.set(title);
    const req = this.selectedRequest();
    if (!req?.uuid) return;
    const st = this.md.parse();
    if (title && !st.dano) this.md.go(this.md.requestDamage(req.uuid));
    else if (!title && st.dano) this.md.go(this.md.request(req.uuid));
  }

  onPageHeaderBack() {
    if (this.claimEditorTitle()) {
      this.requestClaim?.closeDetailEditor();
      this.addClaim?.closeDetailEditor();
      this.claimEditorTitle.set(null);
      const req = this.selectedRequest();
      const st = this.md.parse();
      if (st.area === 'solicitudes' && req?.uuid && st.dano) {
        this.md.go(this.md.request(req.uuid));
      }
      return;
    }
    this.md.back(this.md.inbox());
  }

  onReviewCompleted(keepOpen = false) {
    if (!keepOpen) this.md.go(this.md.inbox());
    this.loadRequests();
  }

  /** Cierra wizard DRAFT. Si envió (true), solo refresca; el wizard navega a la solicitud. */
  onDraftWizardClose(submitted = false): void {
    this.claimEditorTitle.set(null);
    this.loadRequests();
    if (!submitted) {
      this.md.go(this.md.inbox());
    }
  }

  /** Tras alta/envío desde add-claim embebido. */
  onDraftClaimAdded(): void {
    this.claimEditorTitle.set(null);
    this.loadRequests();
  }
}